import type { CanvasMetricsSet } from '@ui/Types/Gnss';
import { customErrorToast } from '@ui/Utils/toasts';

export async function exportSpectrumWaterfallImage(params: {
    axisCanvas: HTMLCanvasElement | null;
    filename?: string;
    metrics: CanvasMetricsSet;
    spectrumCanvas: HTMLCanvasElement | null;
    waterfallCanvas: HTMLCanvasElement | null;
}): Promise<void> {
    const {
        axisCanvas: axis,
        filename = 'sweep-waterfall.png',
        metrics,
        spectrumCanvas: spectrum,
        waterfallCanvas: waterfall,
    } = params;

    if (!axis || !spectrum || !waterfall) {
        customErrorToast({
            message: 'Export failed: canvas not ready',
            options: { id: 'export.failed.canvasNotReady' },
        });
        return;
    }

    const axisDevW = metrics.axis?.devW ?? axis.width;
    const axisDevH = metrics.axis?.devH ?? axis.height;
    const specDevW = metrics.spectrum?.devW ?? spectrum.width;
    const specDevH = metrics.spectrum?.devH ?? spectrum.height;
    const wfDevW = metrics.waterfall?.devW ?? waterfall.width;
    const wfDevH = metrics.waterfall?.devH ?? waterfall.height;

    const parts: { canvas: HTMLCanvasElement; h: number; w: number }[] = [];
    if (axisDevW > 0 && axisDevH > 0) {
        parts.push({ canvas: axis, h: axisDevH, w: axisDevW });
    }
    if (specDevW > 0 && specDevH > 0) {
        parts.push({ canvas: spectrum, h: specDevH, w: specDevW });
    }
    if (wfDevW > 0 && wfDevH > 0) {
        parts.push({ canvas: waterfall, h: wfDevH, w: wfDevW });
    }

    if (parts.length === 0) {
        customErrorToast({
            message: 'Nothing to export: canvases are empty',
            options: { id: 'export.nothingToExport' },
        });
        return;
    }

    const outW = parts.reduce((m, p) => (p.w > m ? p.w : m), 0);
    const outH = parts.reduce((sum, p) => sum + p.h, 0);

    const offscreen = document.createElement('canvas');
    offscreen.width = outW;
    offscreen.height = outH;
    const ctx = offscreen.getContext('2d');
    if (ctx === null) {
        customErrorToast({
            message: 'Export failed: cannot create canvas',
            options: { id: 'export.failed.cannotCreateCanvas' },
        });
        return;
    }

    // Background to match UI
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, outW, outH);

    // Composite canvases top-to-bottom, skipping empty ones
    let y = 0;
    for (const p of parts) {
        ctx.drawImage(p.canvas, 0, y);
        y += p.h;
    }

    await new Promise<void>((resolve) => {
        offscreen.toBlob((blob) => {
            if (blob === null) {
                customErrorToast({
                    message: 'Export failed: image encoding error',
                    options: { id: 'export.failed.imageEncodingError' },
                });
                resolve();
                return;
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.append(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            resolve();
        }, 'image/png');
    });
}

export function exportSweepRowsCsv(params: {
    drawConfig: { binWidthMHz: number; freqStartMHz: number } | null;
    filename?: string;
    rows: number[][];
}): void {
    const { drawConfig, filename = 'sweep-waterfall.csv', rows } = params;

    if (rows.length === 0) {
        customErrorToast({
            message: 'Nothing to export: no sweep data',
            options: { id: 'export.nothingToExport' },
        });
        return;
    }

    const maxCols = rows.reduce((m, r) => {
        if (r.length > m) {
            return r.length;
        }
        return m;
    }, 0);

    const freqStart = drawConfig?.freqStartMHz ?? null;
    const binWidth = drawConfig?.binWidthMHz ?? null;
    const header = ['row'].concat(
        Array.from({ length: maxCols }).map((_, i) => {
            if (freqStart !== null && binWidth !== null) {
                const freqVal = freqStart + i * binWidth;
                const roundedMHz = Math.round(freqVal);
                return `bin_${roundedMHz.toString()}MHz`;
            }
            return `bin_${i.toString()}`;
        }),
    );

    const lines: string[] = [];
    lines.push(header.join(','));
    for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i] ?? [];
        const paddedStrs = Array.from({ length: maxCols }, (_, j) => (j < row.length ? String(row[j]) : ''));
        const line = [String(i)].concat(paddedStrs);
        lines.push(line.join(','));
    }

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.append(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}
